import React from 'react'
import { Card, Flex, Text } from '@tremor/react'
import { Trophy, Target, Zap, TrendingUp, CheckCircle2 } from '@/components/icons'
import type { BonusProgress as BonusProgressType } from '../types'
import { cn } from '@/lib/utils'

interface BonusProgressProps {
  bonus: BonusProgressType
}

interface StatBlockProps {
  label: string
  value: string | number
  icon: React.ReactNode
  highlight?: boolean
}

function StatBlock({ label, value, icon, highlight = false }: StatBlockProps) {
  return (
    <div className={cn(
      "px-4 py-3 rounded-lg transition-colors",
      highlight ? "bg-amber-500/10 border border-amber-500/20" : "bg-zinc-800/50"
    )}>
      <Flex alignItems="center" className="gap-2 mb-1">
        <span className="text-zinc-500">{icon}</span>
        <Text className="text-zinc-500 text-xs uppercase tracking-wide">{label}</Text>
      </Flex>
      <span className={cn(
        "font-mono text-2xl font-bold",
        highlight ? "text-amber-400" : "text-white"
      )}>
        {value}
      </span>
    </div>
  )
}

export function BonusProgress({ bonus }: BonusProgressProps) {
  const isUnlocked = bonus.unlocked
  const progressPercent = bonus.progressPercentage

  return (
    <Card
      className={cn(
        "relative overflow-hidden border-0 animate-fade-in",
        isUnlocked
          ? "bg-gradient-to-r from-emerald-950 to-emerald-900"
          : "bg-gradient-to-r from-zinc-950 to-zinc-900"
      )}
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
      </div>

      <div className="relative z-10">
        {/* Header */}
        <Flex justifyContent="between" alignItems="start">
          <div>
            <Flex alignItems="center" className="gap-2 mb-2">
              <Trophy className={cn(
                "w-6 h-6",
                isUnlocked ? "text-amber-400" : "text-zinc-400"
              )} />
              <Text className="text-zinc-400 uppercase tracking-wider text-xs font-semibold">
                Team Bonus Status
              </Text>
            </Flex>

            <Flex alignItems="baseline" className="gap-3">
              <span className={cn(
                "font-mono text-5xl font-black tracking-tight",
                isUnlocked ? "text-emerald-400" : "text-white"
              )}>
                {bonus.currentRate.toFixed(1)}%
              </span>
              <span className="text-zinc-500 text-lg">
                / {bonus.targetRate}% target
              </span>
            </Flex>
          </div>

          {/* Status Badge */}
          <div className={cn(
            "px-4 py-2 rounded-lg font-mono text-sm font-bold uppercase tracking-wider",
            isUnlocked
              ? "bg-emerald-500 text-white"
              : "bg-zinc-700 text-zinc-300"
          )}>
            {isUnlocked ? (
              <Flex alignItems="center" className="gap-2">
                <Zap className="w-4 h-4" />
                BONUS UNLOCKED
              </Flex>
            ) : (
              <Flex alignItems="center" className="gap-2">
                <Target className="w-4 h-4" />
                IN PROGRESS
              </Flex>
            )}
          </div>
        </Flex>

        {/* Progress Bar - Bold Industrial */}
        <div className="mt-6 mb-4">
          <div className="relative h-4 bg-zinc-800 rounded-full overflow-hidden">
            {/* Target Line */}
            <div
              className="absolute top-0 bottom-0 w-0.5 bg-amber-400 z-10"
              style={{ left: '100%', transform: 'translateX(-100%)' }}
            />
            {/* Progress Fill */}
            <div
              className={cn(
                "h-full rounded-full transition-all duration-700 ease-out",
                isUnlocked
                  ? "bg-gradient-to-r from-emerald-600 to-emerald-400"
                  : "bg-gradient-to-r from-blue-600 to-blue-400"
              )}
              style={{ width: `${Math.min(progressPercent, 100)}%` }}
            />
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-3 gap-4 mt-6">
          <StatBlock
            label="Booked"
            value={bonus.booked}
            icon={<CheckCircle2 className="w-4 h-4" />}
          />
          <StatBlock
            label="Opportunities"
            value={bonus.opportunities}
            icon={<Target className="w-4 h-4" />}
          />
          <StatBlock
            label="Need More"
            value={bonus.bookingsNeeded > 0 ? bonus.bookingsNeeded : '-'}
            icon={<TrendingUp className="w-4 h-4" />}
            highlight={bonus.bookingsNeeded > 0}
          />
        </div>
      </div>
    </Card>
  )
}
